import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Table, Modal, Form, Badge, Spinner, Card, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { BASE_URL } from "../../data/BaseUrl";
import { selectToken } from "../../components/auth/auth.selector";

// ─── API helpers (kept inline — small surface, easy to relocate later) ───────

const ENDPOINT = "/meta-integration";

async function api(method, path, token, body) {
  const res = await fetch(`${BASE_URL}${ENDPOINT}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.status === false) {
    throw new Error(json.message || `HTTP ${res.status}`);
  }
  return json.data;
}

const listIntegrations = (t) => api("GET", "/", t);
const createIntegration = (t, body) => api("POST", "/", t, body);
const updateIntegration = (t, id, body) => api("PUT", `/${id}`, t, body);
const toggleIntegration = (t, id) => api("PATCH", `/${id}/toggle`, t);
const deleteIntegration = (t, id) => api("DELETE", `/${id}`, t);
const getHealth = (t) => api("GET", "/health", t);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const EMPTY_FORM = {
  page_id: "",
  page_name: "",
  access_token: "",
  verify_token: "",
  form_mappings_json: "",
};

// ─── Edit / Add modal ────────────────────────────────────────────────────────

function IntegrationFormModal({ show, initial, onHide, onSubmit, saving }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(EMPTY_FORM);
  const [jsonError, setJsonError] = useState(null);

  useEffect(() => {
    if (!show) return;
    if (initial) {
      setForm({
        page_id: initial.page_id || "",
        page_name: initial.page_name || "",
        access_token: "", // never pre-fill — leave blank to keep existing
        verify_token: initial.verify_token || "",
        form_mappings_json: initial.form_mappings
          ? JSON.stringify(initial.form_mappings, null, 2)
          : "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setJsonError(null);
  }, [show, initial]);

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    let form_mappings = null;
    if (form.form_mappings_json.trim()) {
      try {
        form_mappings = JSON.parse(form.form_mappings_json);
      } catch (err) {
        setJsonError(err.message);
        return;
      }
    }
    const payload = {
      page_id: form.page_id.trim(),
      page_name: form.page_name.trim(),
      verify_token: form.verify_token.trim() || null,
      form_mappings,
    };
    if (form.access_token.trim()) payload.access_token = form.access_token.trim();
    onSubmit(payload);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? `Edit ${initial.page_name}` : "Add Meta page"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Page ID</Form.Label>
            <Form.Control
              required
              value={form.page_id}
              onChange={handleChange("page_id")}
              disabled={isEdit}
              placeholder="e.g. 102345678901234"
            />
            <Form.Text className="text-muted">
              Meta page id. Used as the lookup key on every incoming webhook.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Page name</Form.Label>
            <Form.Control
              required
              value={form.page_name}
              onChange={handleChange("page_name")}
              placeholder="Human-readable label, shown on the dashboard"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Page access token</Form.Label>
            <Form.Control
              type="password"
              value={form.access_token}
              onChange={handleChange("access_token")}
              placeholder={isEdit ? "Leave blank to keep the existing token" : "Page Access Token with leads_retrieval"}
            />
            <Form.Text className="text-muted">
              Encrypted (AES-256-GCM) before it ever lands in the DB. Server never returns it.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Verify token (optional)</Form.Label>
            <Form.Control
              value={form.verify_token}
              onChange={handleChange("verify_token")}
              placeholder="Per-page override; env META_WEBHOOK_VERIFY_TOKEN wins if set"
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Form mappings (JSON)</Form.Label>
            <Form.Control
              as="textarea"
              rows={9}
              value={form.form_mappings_json}
              onChange={handleChange("form_mappings_json")}
              placeholder={'{\n  "<form_id>": {\n    "gymAlias": "hsr-layout-sector-7",\n    "gymAliasField": "do_you_live_near_hsr_layout_sector_7?",\n    "isPt": 0,\n    "preferredSlot": "08:00 am"\n  }\n}'}
              style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
            />
            <Form.Text className="text-muted">
              Per-form gym resolution. <code>gymAlias</code> = fixed alias for gym-specific forms. <code>gymAliasField</code> = answer used as alias for multi-gym forms.
            </Form.Text>
            {jsonError && <div className="text-danger small mt-1">Invalid JSON: {jsonError}</div>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add page"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Health card ─────────────────────────────────────────────────────────────

function HealthCard({ token }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const h = await getHealth(token);
      setHealth(h);
    } catch (e) {
      toast.error(`Health check failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span><strong>Integration health</strong>{health && <span className="text-muted small ms-2">checked {timeAgo(health.checked_at)}</span>}</span>
        <Button size="sm" variant="outline-primary" onClick={refresh} disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" /> : "Refresh"}
        </Button>
      </Card.Header>
      <Card.Body>
        {!health && !loading && <div className="text-muted">No data yet.</div>}
        {health && health.integrations.length === 0 && (
          <Alert variant="info" className="mb-0">No integrations configured yet.</Alert>
        )}
        {health && health.integrations.length > 0 && (
          <Table size="sm" responsive>
            <thead>
              <tr>
                <th>Page</th>
                <th>Token</th>
                <th>Lead count</th>
                <th>Last lead</th>
                <th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {health.integrations.map((h) => (
                <tr key={h.id}>
                  <td>{h.page_name} <span className="text-muted small">({h.page_id})</span></td>
                  <td>
                    {h.token_valid === null
                      ? <Badge bg="secondary">unknown</Badge>
                      : h.token_valid
                        ? <Badge bg="success">valid</Badge>
                        : <Badge bg="danger">invalid</Badge>}
                    {h.token_check_error && <div className="text-danger small mt-1">{h.token_check_error}</div>}
                  </td>
                  <td>{h.lead_count}</td>
                  <td>{timeAgo(h.last_lead_at)}{h.last_lead_age_min !== null && h.last_lead_age_min > 360 && <Badge bg="warning" className="ms-2">stale</Badge>}</td>
                  <td className="text-danger small">{h.last_error || ""}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function MetaIntegration() {
  const token = useSelector(selectToken);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, false = new, object = edit
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setIntegrations(await listIntegrations(token));
    } catch (e) {
      toast.error(`Failed to load integrations: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await updateIntegration(token, editing.id, payload);
        toast.success("Integration updated");
      } else {
        await createIntegration(token, payload);
        toast.success("Integration added");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleIntegration(token, id);
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Delete integration for "${it.page_name}"? This cannot be undone.`)) return;
    try {
      await deleteIntegration(token, it.id);
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-0">Meta Integration</h3>
          <div className="text-muted small">Per-page config for the direct Meta Lead Ads → ODM webhook (replaces make.com).</div>
        </div>
        <Button variant="primary" onClick={() => setEditing(false)}>+ Add Meta page</Button>
      </div>

      <HealthCard token={token} />

      <Card>
        <Card.Header><strong>Connected pages</strong></Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : integrations.length === 0 ? (
            <Alert variant="info" className="mb-0">
              No Meta pages connected yet. Click <strong>Add Meta page</strong> above to configure one.
            </Alert>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Status</th>
                  <th>Forms mapped</th>
                  <th>Leads</th>
                  <th>Last lead</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {integrations.map((it) => (
                  <tr key={it.id}>
                    <td>
                      <div>{it.page_name}</div>
                      <div className="text-muted small">{it.page_id}</div>
                    </td>
                    <td>
                      {it.is_active
                        ? <Badge bg="success">active</Badge>
                        : <Badge bg="secondary">inactive</Badge>}
                    </td>
                    <td>{it.form_mappings ? Object.keys(it.form_mappings).length : 0}</td>
                    <td>{it.lead_count}</td>
                    <td>{timeAgo(it.last_lead_at)}</td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => handleToggle(it.id)}>
                        {it.is_active ? "Disable" : "Enable"}
                      </Button>
                      <Button size="sm" variant="outline-primary" className="me-1" onClick={() => setEditing(it)}>Edit</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(it)}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <IntegrationFormModal
        show={editing !== null}
        initial={editing || null}
        onHide={() => setEditing(null)}
        onSubmit={handleSubmit}
        saving={saving}
      />
    </div>
  );
}
